/**
 * FindingsSummary Component
 * Container component that fetches findings data and displays donut chart visualization
 * Design based on Figma specifications
 */

import { Skeleton } from "@nextui-org/react";
import { useNavigate } from "react-router-dom";
import { useCallback } from "react";
import { ROUTES } from "@/shared/constants/routes";
import { DonutChart } from "./DonutChart";
import { SectionError } from "./SectionError";
import { useGetFindingsSummary } from "../requests/getFindingsSummary";

/**
 * Loading skeleton for findings summary
 */
const FindingsSummarySkeleton = () => (
  <div className="flex flex-col justify-center items-center p-4 gap-4 bg-white shadow-[0px_1px_3px_rgba(0,0,0,0.1),0px_1px_2px_rgba(0,0,0,0.06)] rounded-lg h-[356px]">
    <div className="flex flex-col justify-center items-start gap-1 self-stretch">
      <Skeleton className="h-5 w-48 rounded" />
    </div>
    <div className="flex flex-row items-center gap-3 w-full h-72">
      {/* Chart skeleton */}
      <div className="flex flex-row items-center justify-center px-9 py-12 w-[264px] h-full">
        <Skeleton className="w-48 h-48 rounded-full" />
      </div>
      {/* Legend skeleton */}
      <div className="flex flex-col justify-center items-start px-4 gap-6 w-[264px] flex-grow">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="flex flex-row items-center gap-2 w-full">
            <Skeleton className="w-3 h-3 rounded-full" />
            <Skeleton className="h-5 w-20 rounded flex-grow" />
            <Skeleton className="h-5 w-10 rounded" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

/**
 * Main FindingsSummary component
 */
export const FindingsSummary = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useGetFindingsSummary();

  // Handle severity click - navigate to issues page with severity filter
  const handleSeverityClick = useCallback(
    (severity: "Critical" | "High" | "Medium" | "Low") => {
      navigate(`/${ROUTES.THIRD_PARTY_ISSUES}?severity=${severity}`);
    },
    [navigate]
  );

  // Loading state
  if (isLoading) {
    return <FindingsSummarySkeleton />;
  }

  // Error state
  if (isError || !data) {
    return (
      <div className="flex flex-col justify-center items-center p-4 gap-4 bg-white shadow-[0px_1px_3px_rgba(0,0,0,0.1),0px_1px_2px_rgba(0,0,0,0.06)] rounded-lg h-[356px]">
        <div className="flex flex-col justify-center items-start gap-1 self-stretch">
          <h3 className="text-sm leading-5">
            <span className="font-medium text-[#0F172B]">Findings Summary</span>{' '}
            <span className="font-normal text-[#71717A]">(Issues)</span>
          </h3>
        </div>
        <SectionError
          title="Failed to load findings summary"
          description="Unable to fetch findings data"
          onRetry={() => refetch()}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col justify-center items-center p-4 gap-4 bg-white shadow-[0px_1px_3px_rgba(0,0,0,0.1),0px_1px_2px_rgba(0,0,0,0.06)] rounded-lg h-[356px] w-full">
      {/* Header */}
      <div className="flex flex-col justify-center items-start gap-1 self-stretch">
        <h3 className="text-sm leading-5">
          <span className="font-medium text-[#0F172B]">Findings Summary</span>{' '}
          <span className="font-normal text-[#71717A]">(Issues)</span>
        </h3>
      </div>

      {/* Donut Chart with Legend */}
      <DonutChart
        critical={data.by_severity.critical.count}
        high={data.by_severity.high.count}
        medium={data.by_severity.medium.count}
        low={data.by_severity.low.count}
        criticalPercentage={data.by_severity.critical.percentage}
        highPercentage={data.by_severity.high.percentage}
        mediumPercentage={data.by_severity.medium.percentage}
        lowPercentage={data.by_severity.low.percentage}
        total={data.total_findings}
        onSeverityClick={handleSeverityClick}
      />
    </div>
  );
};

export default FindingsSummary;
